// Director provider orchestration — DeepSeek text prompts, exposes save-draft path.
import type { RecoverableAction, StructuredDirectorError } from '../prompts/errors.js';
import type { PromptRequest } from '../prompts/types.js';
import {
  executeTextProvider,
  type TextAdapterDeps,
  type TextAdapterResult,
} from './textAdapter.js';

export interface DirectorExecuteSuccess<T> {
  ok: true;
  data: T;
  manifest: PromptRequest['provenance'];
  fallbackUsed: boolean;
  fallbackNotice: string | null;
  providerAttempted: boolean;
}

export interface DirectorExecuteFailure<T> {
  ok: false;
  error: StructuredDirectorError;
  uiMessage: string;
  actions: RecoverableAction[];
  manifest: PromptRequest['provenance'];
  fallbackData?: T;
  saveDraftEligible: boolean;
}

export type DirectorExecuteResult<T> = DirectorExecuteSuccess<T> | DirectorExecuteFailure<T>;

export interface DirectorDeps {
  text?: TextAdapterDeps;
}

/** Apply deterministic fallback after a recoverable failure (explicit user/caller choice). */
export function applyDirectorFallback<T>(request: PromptRequest): DirectorExecuteSuccess<T> {
  const data = request.fallback.local() as T;
  return {
    ok: true,
    data,
    manifest: {
      ...request.provenance,
      provider: 'local',
      fallbackUsed: true,
      validationStatus: 'fallback',
    },
    fallbackUsed: true,
    fallbackNotice: 'Local fallback applied after provider failure.',
    providerAttempted: true,
  };
}

/** Save draft path — return invalid manifest without mutating draft answers. */
export function buildSaveDraftResult<T>(
  request: PromptRequest,
  error: StructuredDirectorError,
  uiMessage: string,
): DirectorExecuteFailure<T> {
  return {
    ok: false,
    error,
    uiMessage,
    actions: [...new Set<RecoverableAction>([...error.actions, 'save_draft'])],
    manifest: { ...request.provenance, validationStatus: 'invalid' },
    saveDraftEligible: true,
  };
}

export async function executeDirectorPrompt<T = unknown>(
  request: PromptRequest,
  deps: DirectorDeps = {},
): Promise<DirectorExecuteResult<T>> {
  return executeTextProvider<T>(request, deps.text);
}

export type { TextAdapterResult };
